import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "src/components/buttons/Button";
import StatusCard from "src/components/cards/StatusCard";
import Input, { ErrorMessage } from "src/components/inputs/Input";
import useNotification from "src/hooks/useNotification";
import { CheckBoxIcon } from "src/icons/CheckboxIcon";
import { boolean, object, ref, string } from "yup";
import PublicLayout from "../PublicLayout";
import { useRegister } from "../api-client";
import TermsAndConditionModal from "./TermsAndConditionModal";

const FORM_FIELDS = {
  NAME: "name",
  EMAIL: "email",
  PASSWORD: "password",
  CONFIRM_PASSWORD: "confirmPassword",
  TERMS_AND_CONDITION: "termsAndCondition",
};

const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i; // Custom regex for validating emails

const Register = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { errorNotification } = useNotification();
  const { isLoading, isSuccess, error, execute: register } = useRegister();
  const { values, errors, handleSubmit, handleChange, touched, setFieldValue } =
    useFormik({
      initialValues: {
        [FORM_FIELDS.NAME]: "",
        [FORM_FIELDS.EMAIL]: "",
        [FORM_FIELDS.PASSWORD]: "",
        [FORM_FIELDS.CONFIRM_PASSWORD]: "",
        [FORM_FIELDS.TERMS_AND_CONDITION]: false,
      },
      validationSchema: object({
        [FORM_FIELDS.NAME]: string().required("This is a Required field."),
        [FORM_FIELDS.EMAIL]: string()
          .matches(emailRegex, "Invalid email format")
          .required("This is a Required field."),
        [FORM_FIELDS.PASSWORD]: string().required("This is a Required field."),
        [FORM_FIELDS.CONFIRM_PASSWORD]: string()
          .required("This is a Required field.")
          .oneOf([ref(FORM_FIELDS.PASSWORD), ""], "Passwords must match"),
        [FORM_FIELDS.TERMS_AND_CONDITION]: boolean().oneOf(
          [true],
          "Please accept the Terms & Condition"
        ),
      }),
      onSubmit: ({ name, email, password }) => {
        register({ name, email, password });
      },
    });

  useEffect(() => {
    if (error) {
      const errorStatus = error?.cause?.status;
      if (errorStatus === 409) {
        errorNotification("Account already exist with this email");
      } else {
        errorNotification();
      }
    }
  }, [error, errorNotification]);

  const isSuccessFull = useMemo(() => isSuccess && !error, [error, isSuccess]);

  console.log("line 68 ", isSuccess, isSuccessFull);

  return (
    <>
      <TermsAndConditionModal
        isOpen={isOpen}
        closeModal={() => setIsOpen(false)}
      />
      <PublicLayout
        leftSection={{
          heading: "Join the Future of Customer Management!",
          features: [
            {
              text: "Create your account in just a few clicks and unlock a world of exclusive benefits.",
            },
            {
              text: "Join us and start experiencing a seamless and personalized shopping journey!",
            },
          ],
        }}
        rightSection={
          isSuccess
            ? {}
            : {
                heading: "Sign Up",
                subHeading:
                  "Register your account now to unlock all features and benefits.",
              }
        }
      >
        {isSuccess ? (
          <StatusCard
            title="Verify your email address."
            subtitle="We have sent you an confirmation email click on that in order to verify your email address."
          />
        ) : (
          <div className="flex flex-col">
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <Input
                type="text"
                label="Enter Full Name"
                name={FORM_FIELDS.NAME}
                value={values[FORM_FIELDS.NAME] as string}
                error={
                  touched[FORM_FIELDS.NAME]
                    ? errors[FORM_FIELDS.NAME]
                    : undefined
                }
                onChange={handleChange}
                required
                disabled={isLoading}
              />
              <Input
                // type="email"
                label="Enter Email"
                name={FORM_FIELDS.EMAIL}
                value={values[FORM_FIELDS.EMAIL] as string}
                error={
                  touched[FORM_FIELDS.EMAIL]
                    ? errors[FORM_FIELDS.EMAIL]
                    : undefined
                }
                onChange={handleChange}
                required
                disabled={isLoading}
              />
              <Input
                type="password"
                label="Enter Password"
                name={FORM_FIELDS.PASSWORD}
                value={values[FORM_FIELDS.PASSWORD] as string}
                error={
                  touched[FORM_FIELDS.PASSWORD]
                    ? errors[FORM_FIELDS.PASSWORD]
                    : undefined
                }
                onChange={handleChange}
                required
                disabled={isLoading}
              />

              <Input
                type="password"
                label="Re-Enter Password"
                name={FORM_FIELDS.CONFIRM_PASSWORD}
                value={values[FORM_FIELDS.CONFIRM_PASSWORD] as string}
                error={
                  touched[FORM_FIELDS.CONFIRM_PASSWORD]
                    ? errors[FORM_FIELDS.CONFIRM_PASSWORD]
                    : undefined
                }
                onChange={handleChange}
                required
                disabled={isLoading}
              />

              <div className="flex flex-col gap-3 justify-between text-sm text-right mt-6 items-start">
                <div className="flex gap-2 items-center">
                  <CheckBoxIcon
                    checked={values[FORM_FIELDS.TERMS_AND_CONDITION] as boolean}
                    onClick={(value) =>
                      setFieldValue(FORM_FIELDS.TERMS_AND_CONDITION, value)
                    }
                  />
                  <div className="flex gap-1 items-center">
                    Accept{" "}
                    <span
                      className="text-primary cursor-pointer"
                      onClick={() => setIsOpen(true)}
                    >
                      Terms & Conditions
                    </span>
                  </div>
                </div>
                <ErrorMessage
                  text={
                    touched[FORM_FIELDS.TERMS_AND_CONDITION]
                      ? errors[FORM_FIELDS.TERMS_AND_CONDITION]
                      : undefined
                  }
                />
              </div>

              <Button
                className={"mt-[50px] w-[70%]"}
                type="submit"
                text={isLoading ? "Loading..." : "Register"}
                primary
              />
            </form>

            <p className="mt-[15px] text-left text-sm text-gray-500 gap-2">
              Already have an account ?{" "}
              <span
                onClick={() => navigate("/login")}
                className="font-semibold leading-6 text-primary hover:text-primary-800 cursor-pointer"
              >
                Sign In
              </span>
            </p>
          </div>
        )}
      </PublicLayout>
    </>
  );
};

export default Register;
